import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Landing.css';

const Landing = () => {
    const navigate = useNavigate();
    const [showAnimation, setShowAnimation] = useState(true);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showInstallBtn, setShowInstallBtn] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const containerRef = useRef(null);
    const yearRef = useRef(null);

    // Handle mouse movement for parallax effect
    const handleMouseMove = useCallback((e) => {
        if (containerRef.current) {
            const { clientX, clientY } = e;
            const { width, height } = containerRef.current.getBoundingClientRect();
            const x = (clientX / width - 0.5) * 20;
            const y = (clientY / height - 0.5) * 20;
            setMousePosition({ x, y });
        }
    }, []);

    useEffect(() => {
        // Set current year in footer
        if (yearRef.current) {
            yearRef.current.textContent = new Date().getFullYear();
        }

        // Trigger entrance animation
        setTimeout(() => setIsVisible(true), 100);

        // PWA install prompt
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            if (!window.matchMedia('(display-mode: standalone)').matches) {
                setShowInstallBtn(true);
            }
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        // Check if app is already installed
        if (window.matchMedia('(display-mode: standalone)').matches) {
            setShowInstallBtn(false);
        }

        // Hide animation after it completes
        const animationTimer = setTimeout(() => {
            setShowAnimation(false);
        }, 3500);

        // Create background doves with better performance
        const cleanupDoves = createBackgroundDoves();

        // Preload dove animation
        const preloadTimer = setTimeout(() => {
            const dove = document.querySelector('.rising-dove i');
            if (dove) {
                dove.style.animation = 'whiteWingFlap 0.5s ease-in-out infinite alternate';
            }
        }, 100);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            clearTimeout(animationTimer);
            clearTimeout(preloadTimer);
            cleanupDoves();
        };
    }, []);

    const createBackgroundDoves = () => {
        const doveElements = [];
        const container = containerRef.current || document.body;

        for (let i = 0; i < 8; i++) {
            const dove = document.createElement('div');
            dove.className = 'floating-dove-bg';
            dove.style.left = `${Math.random() * 100}%`;
            dove.style.top = `${Math.random() * 100}%`;
            dove.style.animationDelay = `${Math.random() * 8}s`;
            dove.style.animationDuration = `${15 + Math.random() * 10}s`;
            dove.style.fontSize = `${Math.random() * 30 + 20}px`;
            dove.style.opacity = '0.1';
            dove.style.color = '#ffffff';
            dove.innerHTML = '🕊️';
            container.appendChild(dove);
            doveElements.push(dove);
        }

        // Return cleanup function
        return () => {
            doveElements.forEach(dove => dove.remove());
        };
    };

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;
        
        try {
            await deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            
            if (outcome === 'accepted') {
                console.log('User accepted the install prompt');
                setShowInstallBtn(false);
            }
            setDeferredPrompt(null);
        } catch (error) {
            console.error('Error installing PWA:', error);
        }
    };

    const handleNavigation = (path) => {
        navigate(path);
    };

    return (
        <div 
            className="landing-root"
            ref={containerRef}
            onMouseMove={handleMouseMove}
        >
            {/* Animated Egyptian blue gradient background */}
            <div className="landing-gradient-bg"></div>
            
            {/* White dove patterns overlay */}
            <div className="landing-dove-pattern"></div>

            {/* Dove animation overlay */}
            {showAnimation && (
                <div className="dove-animation-container" id="doveAnimation">
                    <div className="rising-dove-wrapper">
                        <div className="rising-dove">
                            <i className="fas fa-dove" style={{ color: '#ffffff' }}></i>
                        </div>
                        <div className="dove-trail"></div>
                    </div>
                    <div className="animation-text">
                        <span>We are a Family</span>
                    </div>
                </div>
            )}

            {/* Main content */}
            <div className={`landing-content ${isVisible ? 'visible' : ''}`}>
                {/* Decorative white doves with parallax effect */}
                <div 
                    className="landing-decoration decoration-1"
                    style={{ 
                        transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px)` 
                    }}
                >
                    <i className="fas fa-dove" style={{ color: '#ffffff' }}></i>
                </div>
                <div 
                    className="landing-decoration decoration-2"
                    style={{ 
                        transform: `translate(${mousePosition.x * -0.3}px, ${mousePosition.y * -0.3}px)` 
                    }}
                >
                    <i className="fas fa-dove" style={{ color: '#ffffff' }}></i>
                </div>
                <div 
                    className="landing-decoration decoration-3"
                    style={{ 
                        transform: `translate(${mousePosition.x * 0.2}px, ${mousePosition.y * 0.2}px)` 
                    }}
                >
                    <i className="fas fa-dove" style={{ color: '#ffffff' }}></i>
                </div>

                {/* Logo/Icon - White Dove */}
                <div className="landing-logo-wrapper">
                    <div className="landing-logo-glow"></div>
                    <i className="fas fa-dove landing-logo-icon" style={{ color: '#ffffff' }}></i>
                </div>

                {/* Main title section */}
                <div className="landing-title-section">
                    <h1 className="landing-h1">
                        <span className="gradient-text">WELCOME TO</span>
                        <span className="highlight-text">WHITE DOVES</span>
                    </h1>
                    
                    <div className="landing-accent-line">
                        <span className="line-star">✧</span>
                        <span className="line"></span>
                        <span className="line-star">✧</span>
                    </div>
                    
                    <p className="landing-tagline">
                        <span className="tagline-part">Spreading God's Love</span>
                        <span className="tagline-part">and Peace to All Nations</span>
                    </p>
                </div>

                {/* Action buttons with hover effects */}
                <div className="landing-action-buttons">
                    <button 
                        className="landing-btn landing-btn-primary"
                        onClick={() => handleNavigation('/register')}
                    >
                        <span className="btn-content">
                            <i className="fas fa-user-plus"></i>
                            <span>Signup</span>
                        </span>
                        <span className="btn-shine"></span>
                    </button>
                    
                    <button 
                        className="landing-btn landing-btn-secondary"
                        onClick={() => handleNavigation('/login')}
                    >
                        <span className="btn-content">
                            <i className="fas fa-user"></i>
                            <span>Sign In</span>
                        </span>
                    </button>
                </div>

                {/* Social links with tooltips */}
                <div className="landing-social-row">
                    <a 
                        href="#" 
                        className="landing-social-icon" 
                        aria-label="Facebook"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-tooltip="Follow us on Facebook"
                    >
                        <i className="fab fa-facebook-f"></i>
                        <span className="social-tooltip">Facebook</span>
                    </a>
                    
                    <a 
                        href="https://www.tiktok.com/@white.doves.minis" 
                        className="landing-social-icon" 
                        aria-label="TikTok"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-tooltip="Follow us on TikTok"
                    >
                        <i className="fab fa-tiktok"></i>
                        <span className="social-tooltip">TikTok</span>
                    </a>
                    
                    <a 
                        href="https://wa.me/+254114963959" 
                        className="landing-social-icon" 
                        aria-label="WhatsApp"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-tooltip="Chat with us on WhatsApp"
                    >
                        <i className="fab fa-whatsapp"></i>
                        <span className="social-tooltip">WhatsApp</span>
                    </a>
                    
                    <a 
                        href="#" 
                        className="landing-social-icon" 
                        aria-label="Instagram"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-tooltip="Follow us on Instagram"
                    >
                        <i className="fab fa-instagram"></i>
                        <span className="social-tooltip">Instagram</span>
                    </a>
                    
                    <a 
                        href="https://www.youtube.com/@whitedovesministry-kusda" 
                        className="landing-social-icon" 
                        aria-label="YouTube"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-tooltip="Subscribe on YouTube"
                    >
                        <i className="fab fa-youtube"></i>
                        <span className="social-tooltip">YouTube</span>
                    </a>
                </div>

                {/* Footer with quick links */}
                <div className="landing-footer">
                    <div className="footer-content">
                        <p className="copyright">
                            © <span ref={yearRef}>{new Date().getFullYear()}</span> 
                            <span className="footer-brand"> White Doves Ministry</span>
                        </p>
                        
                        <div className="footer-links">
                            <a href="/privacy" className="footer-link">Privacy</a>
                            <span className="footer-separator">•</span>
                            <a href="/terms" className="footer-link">Terms</a>
                            <span className="footer-separator">•</span>
                            <a href="/contact" className="footer-link">Contact</a>
                        </div>
                        
                        <p className="footer-credit">
                            <i className="fas fa-code"></i> Toplink solutions
                        </p>
                    </div>
                </div>
            </div>

            {/* Install button with animation */}
            {showInstallBtn && (
                <button 
                    className="landing-install-btn"
                    onClick={handleInstallClick}
                >
                    <i className="fas fa-download"></i>
                    <span className="install-text">Install App</span>
                    <span className="install-subtext">Get offline access</span>
                </button>
            )}

        </div>
    );
};

export default Landing;
